import { useCallback, useEffect, useRef, useState } from 'react'
import { Dialogo } from './Dialogo'
import './escaner.css'

/*
 * Leer codigos de barras con la camara del equipo.
 *
 * No reemplaza a la pistola: es otra fuente para el mismo campo. El lector
 * sigue funcionando como teclado —escribe el codigo y manda Enter— y quien
 * tenga uno no va a usar esto. Existe para el local que todavia no lo compro:
 * con una tablet y nada mas, el sistema ya sirve, y el lector pasa a ser una
 * mejora que se compra cuando el volumen la pide.
 *
 * Se apoya en BarcodeDetector, que Chrome trae de fabrica en Android. No se
 * agrega ninguna dependencia a proposito: una libreria de decodificacion son
 * varios cientos de kilobytes de WebAssembly, y este paquete lo descarga una
 * tablet del mostrador. Donde el navegador no lo trae, la pantalla lo dice y
 * el resto del sistema sigue igual.
 */

/* -------------------------------------------------------------------------- */
/* BarcodeDetector no esta en las definiciones de TypeScript todavia.          */

interface CodigoDetectado {
    rawValue: string
    format: string
}

interface DetectorCodigos {
    detect(fuente: HTMLVideoElement): Promise<CodigoDetectado[]>
}

interface ConstructorDetector {
    new (opciones?: { formats?: string[] }): DetectorCodigos
    getSupportedFormats?(): Promise<string[]>
}

declare global {
    interface Window {
        BarcodeDetector?: ConstructorDetector
    }
}

/**
 * Los formatos que llevan los productos de almacen. Acotar la lista hace la
 * deteccion mas rapida y, sobre todo, evita falsos positivos: sin esto el
 * detector busca tambien QR y matrices, que no van a estar en un tarro de
 * atun pero si en el afiche pegado atras de la caja.
 */
const FORMATOS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf']

/** Milisegundos entre lecturas del mismo codigo. */
const ESPERA_REPETIDO = 1500

/** Cada cuanto se mira el cuadro de video. Mas seguido no lee mejor y calienta la tablet. */
const INTERVALO_MS = 200

type Estado =
    | { tipo: 'iniciando' }
    | { tipo: 'leyendo' }
    | { tipo: 'sin_soporte' }
    | { tipo: 'sin_https' }
    | { tipo: 'sin_permiso' }
    | { tipo: 'sin_camara' }
    | { tipo: 'error'; mensaje: string }

interface Props {
    /**
     * 'continuo' sigue leyendo despues de cada codigo: es lo que quiere el
     * cajero pasando productos uno tras otro. 'unico' lee uno y cierra, que es
     * lo que quiere quien esta buscando un producto para editarlo.
     */
    modo?: 'continuo' | 'unico'
    titulo?: string
    alLeer: (codigo: string) => void
    alCerrar: () => void
}

export function EscanerCamara({ modo = 'unico', titulo = 'Escanear con la cámara', alLeer, alCerrar }: Props) {
    const video = useRef<HTMLVideoElement>(null)
    const flujo = useRef<MediaStream | null>(null)
    const ultimo = useRef<{ codigo: string; en: number }>({ codigo: '', en: 0 })

    /*
     * Las devoluciones de llamada van en una referencia y **no** en las
     * dependencias del efecto. Quien usa esto las pasa como flechas en linea,
     * asi que cambian de identidad en cada render del padre; si estuvieran en
     * las dependencias, cada tecla que el cajero escribe en el POS desmontaria
     * y volveria a montar la camara. Se guarda siempre la ultima version.
     */
    const alLeerRef = useRef(alLeer)
    const alCerrarRef = useRef(alCerrar)

    alLeerRef.current = alLeer
    alCerrarRef.current = alCerrar

    const [estado, setEstado] = useState<Estado>({ tipo: 'iniciando' })
    const [leidos, setLeidos] = useState<string[]>([])
    const [linterna, setLinterna] = useState(false)
    const [hayLinterna, setHayLinterna] = useState(false)

    /*
     * Un pitido corto al leer. En un mostrador con ruido, el cajero no mira la
     * pantalla mientras pasa productos: escucha. Se genera con WebAudio en vez
     * de traer un archivo de sonido, que serian bytes de mas en la descarga.
     */
    const sonar = useCallback(() => {
        try {
            const contexto = new AudioContext()
            const oscilador = contexto.createOscillator()
            const volumen = contexto.createGain()

            oscilador.frequency.value = 1760
            volumen.gain.setValueAtTime(0.12, contexto.currentTime)
            volumen.gain.exponentialRampToValueAtTime(0.001, contexto.currentTime + 0.12)

            oscilador.connect(volumen).connect(contexto.destination)
            oscilador.start()
            oscilador.stop(contexto.currentTime + 0.12)

            window.setTimeout(() => void contexto.close(), 300)
        } catch {
            // Sin sonido se sigue pudiendo escanear.
        }

        try {
            navigator.vibrate?.(40)
        } catch {
            // Idem.
        }
    }, [])

    useEffect(() => {
        let vigente = true
        let temporizador = 0

        async function arrancar() {
            // getUserMedia solo existe en contexto seguro. Un POS servido por
            // http contra una IP de la red local no lo tiene, y es el caso mas
            // probable de instalacion chica: por eso el mensaje es explicito y
            // no un "no se pudo acceder a la camara".
            if (!window.isSecureContext) {
                setEstado({ tipo: 'sin_https' })
                return
            }

            if (!window.BarcodeDetector || !navigator.mediaDevices?.getUserMedia) {
                setEstado({ tipo: 'sin_soporte' })
                return
            }

            let detector: DetectorCodigos

            try {
                // Si el navegador soporta la interfaz pero ninguno de estos
                // formatos, construir el detector con la lista falla.
                const soportados = (await window.BarcodeDetector.getSupportedFormats?.()) ?? FORMATOS
                const utiles = FORMATOS.filter((f) => soportados.includes(f))

                if (utiles.length === 0) {
                    setEstado({ tipo: 'sin_soporte' })
                    return
                }

                detector = new window.BarcodeDetector({ formats: utiles })
            } catch {
                setEstado({ tipo: 'sin_soporte' })
                return
            }

            try {
                flujo.current = await navigator.mediaDevices.getUserMedia({
                    video: {
                        // La de atras: la tablet se apunta al producto.
                        facingMode: { ideal: 'environment' },
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                    },
                })
            } catch (fallo) {
                if (!vigente) return

                const nombre = fallo instanceof DOMException ? fallo.name : ''

                setEstado(
                    nombre === 'NotAllowedError' || nombre === 'SecurityError'
                        ? { tipo: 'sin_permiso' }
                        : nombre === 'NotFoundError' || nombre === 'OverconstrainedError'
                          ? { tipo: 'sin_camara' }
                          : { tipo: 'error', mensaje: 'No se pudo abrir la cámara.' },
                )
                return
            }

            if (!vigente || !video.current) {
                flujo.current?.getTracks().forEach((t) => t.stop())
                return
            }

            video.current.srcObject = flujo.current
            await video.current.play().catch(() => undefined)

            const pista = flujo.current.getVideoTracks()[0]
            const capacidades = pista?.getCapabilities?.() as { torch?: boolean } | undefined
            setHayLinterna(Boolean(capacidades?.torch))

            setEstado({ tipo: 'leyendo' })

            temporizador = window.setInterval(async () => {
                if (!vigente || !video.current || video.current.readyState < 2) return

                let encontrados: CodigoDetectado[] = []

                try {
                    encontrados = await detector.detect(video.current)
                } catch {
                    // Un cuadro que no se pudo analizar no es un error: se
                    // intenta con el siguiente.
                    return
                }

                const codigo = encontrados[0]?.rawValue?.trim()

                if (!codigo) return

                const ahora = Date.now()

                // Mismo codigo dos veces seguidas: puede ser que el cajero este
                // pasando dos unidades iguales, o que la camara siga viendo el
                // mismo tarro. No hay forma de distinguirlo, asi que se pide un
                // instante de separacion y el pitido le dice cuando conto.
                if (codigo === ultimo.current.codigo && ahora - ultimo.current.en < ESPERA_REPETIDO) {
                    return
                }

                ultimo.current = { codigo, en: ahora }

                sonar()
                setLeidos((previos) => [codigo, ...previos].slice(0, 5))
                alLeerRef.current(codigo)

                if (modo === 'unico') {
                    alCerrarRef.current()
                }
            }, INTERVALO_MS)
        }

        void arrancar()

        return () => {
            vigente = false
            window.clearInterval(temporizador)

            // Soltar la camara importa: sin esto la luz del equipo queda
            // encendida y la bateria de la tablet se va en una tarde.
            flujo.current?.getTracks().forEach((pista) => pista.stop())
            flujo.current = null
        }
    }, [modo, sonar])

    async function alternarLinterna() {
        const pista = flujo.current?.getVideoTracks()[0]

        if (!pista) return

        try {
            // 'torch' no esta en las definiciones de TypeScript; el navegador
            // si lo entiende, y si no, applyConstraints lanza y se esconde el
            // boton.
            await pista.applyConstraints({
                advanced: [{ torch: !linterna }],
            } as unknown as MediaTrackConstraints)

            setLinterna((actual) => !actual)
        } catch {
            setHayLinterna(false)
        }
    }

    return (
        <Dialogo
            titulo={titulo}
            ancho="ancho"
            alCerrar={alCerrar}
            acciones={
                <>
                    {hayLinterna && (
                        <button type="button" className="boton" onClick={() => void alternarLinterna()}>
                            {linterna ? 'Apagar luz' : 'Encender luz'}
                        </button>
                    )}
                    <button type="button" className="boton boton-primario" onClick={alCerrar}>
                        {modo === 'continuo' ? 'Listo' : 'Cancelar'}
                    </button>
                </>
            }
        >
            <div className="columna g3">
                {estado.tipo === 'leyendo' || estado.tipo === 'iniciando' ? (
                    <div className="escaner-visor">
                        <video ref={video} className="escaner-video" playsInline muted />
                        <div className="escaner-mira" aria-hidden="true" />
                        {estado.tipo === 'iniciando' && (
                            <span className="escaner-aviso">Abriendo la cámara…</span>
                        )}
                    </div>
                ) : (
                    <MensajeSinCamara estado={estado} />
                )}

                {estado.tipo === 'leyendo' && (
                    <p className="tenue chico">
                        Apunte al código de barras del producto. Si está borroso, acerque o aleje
                        unos centímetros.
                    </p>
                )}

                {leidos.length > 0 && (
                    <div className="columna g1">
                        <span className="etiqueta">Leídos</span>
                        <ul className="lista-simple">
                            {leidos.map((codigo, indice) => (
                                <li key={`${codigo}-${indice}`} className="numero chico">
                                    {codigo}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </Dialogo>
    )
}

/* -------------------------------------------------------------------------- */

function MensajeSinCamara({ estado }: { estado: Estado }) {
    if (estado.tipo === 'sin_https') {
        return (
            <div className="aviso aviso-atencion">
                <strong>La cámara necesita una conexión segura.</strong>
                <p style={{ marginTop: 'var(--e2)' }}>
                    El navegador solo deja usar la cámara en sitios <code>https</code>. Este
                    sistema se está viendo por <code>http</code>, así que la lectura con cámara
                    queda deshabilitada.
                </p>
                <p style={{ marginTop: 'var(--e2)' }}>
                    Se resuelve publicando el sistema con un dominio y certificado. Mientras
                    tanto, el lector de código de barras y la búsqueda por nombre funcionan igual.
                </p>
            </div>
        )
    }

    if (estado.tipo === 'sin_permiso') {
        return (
            <div className="aviso aviso-atencion">
                <strong>El navegador no dio permiso para usar la cámara.</strong>
                <p style={{ marginTop: 'var(--e2)' }}>
                    Toque el candado de la barra de direcciones y permita la cámara para este
                    sitio. Después vuelva a abrir esta ventana.
                </p>
            </div>
        )
    }

    if (estado.tipo === 'sin_camara') {
        return (
            <div className="aviso aviso-atencion">
                Este equipo no tiene una cámara que se pueda usar.
            </div>
        )
    }

    if (estado.tipo === 'sin_soporte') {
        return (
            <div className="aviso aviso-atencion">
                <strong>Este navegador no sabe leer códigos de barras.</strong>
                <p style={{ marginTop: 'var(--e2)' }}>
                    Funciona en Chrome sobre Android, que es lo que trae cualquier tablet. En
                    iPad todavía no. El lector de código de barras y la búsqueda por nombre
                    funcionan igual.
                </p>
            </div>
        )
    }

    return (
        <div className="aviso aviso-error" role="alert">
            {estado.tipo === 'error' ? estado.mensaje : 'No se pudo abrir la cámara.'}
        </div>
    )
}

/**
 * Si conviene mostrar el boton de la camara.
 *
 * Se comprueba una sola vez y en el momento de dibujar, no al escanear: un
 * boton que al tocarlo siempre explica por que no anda es peor que no tenerlo.
 * La excepcion es http, donde el boton **si** se muestra: ahi el mensaje le
 * dice al instalador exactamente que le falta, y es informacion que quiere.
 */
export function hayCamaraPosible(): boolean {
    if (typeof window === 'undefined') return false

    if (!window.isSecureContext) return true

    return Boolean(window.BarcodeDetector && navigator.mediaDevices?.getUserMedia)
}
